
import { Logger } from '../utils/Logger';
import { SparseList } from './SparseList';
import { TypedRow, TypedRowHandlerWithErrors, TypedRowStoreWithErrors } from './TypedDataGridInterfaces';

export const DEFAULT_PAGE_SIZE = 250;

/**
 * The number of consecutive calls to getItems whose requested range and returned rowResult match each other's
 * before subsequent calls requesting the same range will stop issuing queries.
 *
 * Note: This will only actually take effect if the backend consistently returns fewer rows than expected.
 */
export const CONSECUTIVE_MATCHING_REQUEST_CUTOFF = 5;

export interface RowResult<TRowData, TError> {
  rows: Readonly<TypedRow<TRowData>>[];
  totalCount: number;
  errors?: ReadonlyArray<TError>;
}

interface Range {
  readonly start: number;
  readonly end: number;
}

/**
 * Client-provided function that uses the applicable backend to fetch the given range of rows.
 * The RowStore will call this when the it wants some rows that it hasn't cached already.
 * Start and end are INCLUSIVE.
 */
export type QueryRows<TRowData, TError> =
  (pageBoundaries: { start: number, end: number }) => Promise<RowResult<TRowData, TError>>;


/**
 * This class implements pagination/caching logic that can be used with any data grid or other list
 * view component. It is particularly useful for situations where we are querying large sets of
 * user-provided data that is not all available at once (in those cases, the basic ArrayRowStore
 * would be sufficient).
 *
 * Using the client-provided `requestRows` function, the row store queries the backend for larger
 * chunks than the component asks for, then caches these fetched items so it can instantly return them
 * back to the component as the user scrolls through the list.
 *
 * Per the RowStore API, the array returned by the row handler is sparse, with the length being equal
 * to the total count of all the items, including those that have not yet been fetched.
 */
export class PaginatedRowStore<TRowData, TError>
  implements TypedRowStoreWithErrors<TRowData, TError> {

  private rows: SparseList<TypedRow<TRowData>> = new SparseList(0);
  private errors: ReadonlyArray<TError> = [];
  private previousRequestedRange: Range = { start: -1, end: -1 };
  private previousRowResult: RowResult<TRowData, TError> = { rows: [], totalCount: 0 };
  private consecutiveMatchingRequests = 1;

  public constructor(
    private queryRows: QueryRows<TRowData, TError>,
    private pageSize: number = DEFAULT_PAGE_SIZE,
  ) { }

  public getItems(start: number, end: number, rowHandler: TypedRowHandlerWithErrors<TRowData, TError>): void {

    const requestedRange = { start, end };

    // Don't repeat requests indefinitely when the results aren't changing
    if (rangesEqual(requestedRange, this.previousRequestedRange)
      && this.consecutiveMatchingRequests >= CONSECUTIVE_MATCHING_REQUEST_CUTOFF
    ) {
      rowHandler([...this.rows.data], this.errors);
      return;
    }

    const normalQueryRange = this.getPossibleQueryRangeForNormalQuery(requestedRange);

    if (normalQueryRange) {
      this.queryRows(normalQueryRange)
        .then(rowResult => {
          this.maybeIncrementDuplicateRequestCounterAndCacheResult(requestedRange, rowResult);
          this.updateRows(rowResult, normalQueryRange);
          // If we don't copy the data to a new array, the rendered grid sections sometimes have holes
          rowHandler([...this.rows.data], this.errors);
        })
        .catch(error => {
          Logger.error(error);
          throw error;
        });

    } else {

      // If we already had the requested group of items, maybe pre-fetch the next page of items
      // so that items will continue to render instantly as the user scrolls down.
      // Don't invoke the rowHandler after prefetching the rows because we don't need to render them immediately.
      const prefetchQueryRange = this.getPossibleQueryRangeForPrefetch(requestedRange);
      if (prefetchQueryRange) {
        this.queryRows(prefetchQueryRange)
          .then(rowResult => {
            this.maybeIncrementDuplicateRequestCounterAndCacheResult(requestedRange, rowResult);
            this.updateRows(rowResult, prefetchQueryRange);
          })
          .catch(error => {
            Logger.error(error);
            throw error;
          });
      }

      // Invoke the rowHandler AFTER executing the query so that we can verify in the tests that the query happened.
      // The query result will still be handled asynchronously, so as not to block the data grid from rendering.
      rowHandler([...this.rows.data], this.errors);
    }
  }

  /**
   * Looks at the whole "page" of items containing the requested range, and returns the boundaries
   * for that page if the page is not completely fetched. If that page is already fetched, returns null.
   */
  private getPossibleQueryRangeForNormalQuery(requestedRange: Range): Range | null {

    // The DataGrid requests small chunks of items (on the order of 20 at a time), so we will usually
    // "extend" the query range to request an entire page of items from the backend, to reduce the
    // number of times we have to call the backend as the user scrolls down.
    //
    // However, if this is the very first request to the row store (i.e. if we don't yet know the total item count),
    // we query for just the bare minimum amount of rows, so that we get a fast initial load time.
    if (!this.rows.length) {
      return requestedRange;
    }

    const queryRange = this.getPageBoundariesForRange(requestedRange);
    return this.itemsMissingInRange(queryRange) ? queryRange : null;
  }

  /**
   * The "prefetch" looks one page ahead from the page on which the currently requested rows reside.
   * If some items are missing on that page, its boundaries are returned; otherwise, returns null.
   */
  private getPossibleQueryRangeForPrefetch(requestedRange: Range): Range | null {

    // Manufacture a one-item range that sits on the next page so that we can calculate
    // that page's full boundaries
    const itemOnNextPage = requestedRange.end + this.pageSize;
    const rangeWithOneItemOnNextPage = { start: itemOnNextPage, end: itemOnNextPage };

    const queryRange = this.getPageBoundariesForRange(rangeWithOneItemOnNextPage);
    return this.itemsMissingInRange(queryRange) ? queryRange : null;
  }

  private getPageBoundariesForRange(range: Range): Range {
    const pageIndexOfFirstItem = Math.floor(range.start / this.pageSize);
    const pageIndexOfLastItem = Math.floor(range.end / this.pageSize);

    if (pageIndexOfFirstItem === pageIndexOfLastItem) {

      // Conventional case: the range lies within a single page; return that full page
      const start = this.pageSize * pageIndexOfFirstItem;
      const end = Math.min(start + this.pageSize - 1, this.rows.length - 1);
      return { start, end };

    } else {

      // If the request straddles a page boundary, we return a range that isn't snapped to page boundaries
      // so we can still return all the requested rows without wasting bandwidth
      return {
        start: range.start,
        end: Math.min(range.start + this.pageSize - 1, this.rows.length - 1),
      };
    }
  }

  private itemsMissingInRange(range: Range): boolean {
    return !this.rows.rangeIsDefined(range.start, range.end);
  }

  private updateRows(rowResult: RowResult<TRowData, TError>, queryRange: Range): void {

    // Invalidate our previous rows if the new query says the row count is different
    // than what we had before. This is the easiest scenario where we know the data has changed.
    // TODO (TFS 873789): Do we need to invalidate Apollo's cache too?
    if (rowResult.totalCount !== this.rows.length) {
      this.rows = new SparseList(rowResult.totalCount);

      this.previousRequestedRange = { start: -1, end: -1 };
      this.previousRowResult = { rows: [], totalCount: 0 };
      this.consecutiveMatchingRequests = 1;
    }

    this.rows.setRange(queryRange.start, rowResult.rows);
    if (rowResult.errors) {
      this.errors = this.errors.concat(rowResult.errors);
    }
  }

  private maybeIncrementDuplicateRequestCounterAndCacheResult(requestedRange: Range, rowResult: RowResult<TRowData, TError>) {
    const requestedRangeWasTheSameAsLastTime = rangesEqual(requestedRange, this.previousRequestedRange);

    if (requestedRangeWasTheSameAsLastTime && resultsEqual(rowResult, this.previousRowResult)) {
      this.consecutiveMatchingRequests++;
    } else {
      this.consecutiveMatchingRequests = 1;
    }

    this.previousRequestedRange = requestedRange;
    this.previousRowResult = rowResult;
  }

  // This method returns a readonly view of the rows currently stored by the row store, for unit tests.
  public getStoredRows(): ReadonlyArray<Readonly<TypedRow<TRowData>>> {
    return this.rows.data;
  }
}

function rangesEqual(a: Range, b: Range): boolean {
  return a.start === b.start && a.end === b.end;
}

function resultsEqual(a: RowResult<unknown, unknown>, b: RowResult<unknown, unknown>) {
  if (a.totalCount !== b.totalCount || a.rows.length !== b.rows.length) {
    return false;
  }

  for (let i = 0; i < a.rows.length; i++) {
    if (a.rows[i].key !== b.rows[i].key) {
      return false;
    }
  }

  return true;
}
